import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../context/AppContext";
import { SuggestedEvidenceTrigger } from "../../components/common/GapQuestionComment";
import { Overlay } from "../../components/common/UI";
import DepartmentDropdown from "../../components/common/DepartmentSelect";
import DepartmentSelectionRequired from "../../components/common/DepartmentSelectionRequired";
import { useDepartments, useAutoSelectAssignedDepartment } from "../../hooks/useDepartments";
import {
  getLiveTourPreviewDepartmentId,
  notifyLiveTourTargetsChanged,
  useLiveTourDepartmentPrep,
  useLiveTourOptional,
} from "../../context/LiveTourContext";
import {
  afterValidationRender,
  isFieldRequired,
  scrollToFirstInvalidField,
} from "../../utils/formValidationFocus";
import FormScorePanel from "../../components/common/FormScorePanel";
import {
  ShieldCheck,
  Info,
  Shield,
  Layers,
  AlertTriangle,
  CheckCircle2,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  X,
  Plus,
  Minus,
  Clock,
  Globe,
  Database,
  Server,
  Upload,
  Zap,
  Lock,
  Eye,
  Scale,
  Users,
  Building2,
  FileText,
  Menu,
  LayoutList,
  RefreshCw,
  Award,
  Download,
} from "lucide-react";
import { exportSubmissionPdf } from "../../utils/exportSubmissionPdf";
import { showError } from "../../utils/sweetAlert";
import { PIA_CONFIG } from "../../components/common/FormSubmissionsPage";
import { useAssessmentCycles } from "../../hooks/useAssessmentCycles";

// ─── Step config ──────────────────────────────────────────────────────────────
const STEPS = [
  {
    id: "general",
    code: "GEN",
    title: "General Info",
    Icon: Info,
    accent: "#6366f1",
    accentLight: "#eef2ff",
    desc: "Project identification, processing context, and legal basis for data processing",
  },
  {
    id: "dataDetails",
    code: "DAT",
    title: "Data Details",
    Icon: Shield,
    accent: "#8b5cf6",
    accentLight: "#f5f3ff",
    desc: "Personal data categories, sensitivity classification, and data subject groups",
  },
  {
    id: "dataFlow",
    code: "FLW",
    title: "Data Flow",
    Icon: Layers,
    accent: "#0891b2",
    accentLight: "#ecfeff",
    desc: "Storage locations, collection methods, and all internal/external transfer pathways",
  },
  {
    id: "risk",
    code: "RSK",
    title: "Risk Assessment",
    Icon: AlertTriangle,
    accent: "#ef4444",
    accentLight: "#fff1f2",
    desc: "Likelihood and impact scoring, risk category, and documented mitigation measures",
  },
  {
    id: "vendorControls",
    code: "VND",
    title: "Vendor & Controls",
    Icon: BookOpen,
    accent: "#f59e0b",
    accentLight: "#fffbeb",
    desc: "Control framework, compliance certifications, and vendor geographic footprint",
  },
  {
    id: "safeguards",
    code: "SAF",
    title: "Safeguards & Review",
    Icon: CheckCircle2,
    accent: "#10b981",
    accentLight: "#f0fdf4",
    desc: "GDPR and DPDP regulatory safeguards, transfer justification, and final sign-off",
  },
];

const LEGAL_BASIS = [
  {
    id: "consent",
    label: "Consent",
    Icon: Check,
    desc: "Explicit consent obtained",
  },
  {
    id: "contract",
    label: "Contract",
    Icon: FileText,
    desc: "Contractual necessity",
  },
  {
    id: "legal",
    label: "Legal Obligation",
    Icon: Scale,
    desc: "Required by law",
  },
  {
    id: "vital",
    label: "Vital Interests",
    Icon: Zap,
    desc: "Vital interests protection",
  },
  {
    id: "legitimate",
    label: "Legitimate Interests",
    Icon: Building2,
    desc: "Legitimate business purposes",
  },
  {
    id: "public",
    label: "Public Task",
    Icon: Globe,
    desc: "Public interest or task",
  },
];

const STORAGE_OPTIONS = [
  "AWS S3",
  "Google Cloud Storage",
  "Azure Blob Storage",
  "On-Premise Server",
  "Other",
];
const DATA_TYPES = [
  "Name",
  "Email",
  "Phone Number",
  "Address",
  "Financial Data",
  "Health Data",
  "Biometric Data",
  "Location Data",
  "IP Address",
  "Cookies / Tracking",
  "Government ID",
  "Employment Data",
];
const COLLECTION_METHODS = [
  "Web Forms",
  "APIs",
  "Mobile Apps",
  "Automated logs",
  "Manual Entry",
  "Third-party Sources",
  "Sensors / IoT",
  "Email / Communication",
];
const CONTROL_TYPES = [
  "Technical",
  "Administrative",
  "Physical",
  "Contractual",
  "Operational",
];
const COMPLIANCE_STATUSES = [
  "GDPR Compliant",
  "DPDP Compliant",
  "HIPAA Compliant",
  "ISO 27001",
  "SOC 2",
  "CCPA Compliant",
  "Non-Compliant",
  "Under Review",
];

const LEVEL_META = {
  1: { label: "Very Low", color: "#10b981" },
  2: { label: "Low", color: "#10b981" },
  3: { label: "Medium", color: "#f59e0b" },
  4: { label: "High", color: "#ef4444" },
  5: { label: "Critical", color: "#7c3aed" },
};

// ─── Arc Gauge (identical to GAP) ─────────────────────────────────────────────
function ArcGauge({
  value,
  size = 68,
  stroke = 7,
  color = "#6366f1",
  bgStrokeColor = "rgba(71, 85, 105, 0.15)",
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <svg
      width={size}
      height={size}
      style={{ transform: "rotate(-90deg)", display: "block" }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={bgStrokeColor}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{
          transition: "stroke-dashoffset 0.9s cubic-bezier(0.4,0,0.2,1)",
        }}
      />
    </svg>
  );
}

// ─── Primitive Fields (inline-style, DM Sans) ─────────────────────────────────
const fld = (focused, error) => ({
  width: "100%",
  fontSize: 12.5,
  padding: "9px 12px",
  borderRadius: 10,
  background: "#ffffff",
  border: `1.5px solid ${error ? "#fca5a5" : focused ? "#6366f1" : "rgba(99,102,241,0.2)"}`,
  color: "#0f172a",
  outline: "none",
  fontFamily: "inherit",
  boxShadow:
    focused && !error
      ? "0 0 0 3px rgba(99,102,241,0.1)"
      : "0 1px 2px rgba(0,0,0,0.04)",
  transition: "border-color 0.15s, box-shadow 0.15s",
});

function getPiaReferenceEvidence(fieldId) {
  if (!fieldId || typeof window === "undefined") return null;
  const map = window.__piaReferenceEvidence || {};
  if (map[fieldId]) return map[fieldId];
  const schemaField = window.__piaSchemaFields?.find(
    (x) => x.external_id === fieldId || x.id === fieldId,
  );
  if (schemaField?.id && map[schemaField.id]) return map[schemaField.id];
  if (schemaField?.external_id && map[schemaField.external_id]) {
    return map[schemaField.external_id];
  }
  return null;
}

function FL({ children, required, optional, fieldId, subsection }) {
  let displaySub = subsection || "";
  let displayId = fieldId;
  const evidence = getPiaReferenceEvidence(fieldId);

  if (fieldId && window.__piaSchemaFields) {
    const f = window.__piaSchemaFields.find((x) => x.external_id === fieldId);
    if (f) {
      // Prefer tenant-admin-edited description over any passed-in subsection
      displaySub = f.description || subsection || "";
      // Also resolve clean generated display ID (e.g. GEN01) for hardcoded fields

      if (!/^[A-Z]{3,4}\d+$/.test(displayId)) {
        const codes = {
          general: "GEN",
          dataDetails: "DAT",
          dataFlow: "FLW",
          risk: "RSK",
          vendorControls: "VND",
          safeguards: "SAF",
        };
        const sectionItems = window.__piaSchemaFields.filter(
          (item) => item.section_id === f.section_id,
        );
        const idx = sectionItems.findIndex(
          (item) => item.external_id === fieldId,
        );
        const num = idx !== -1 ? idx + 1 : 1;
        const secCode = codes[f.section_id] || "GEN";
        displayId = `${secCode}${String(num).padStart(2, "0")}`;
      }
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        marginBottom: 6,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <label
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#1e293b",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          {children}
          {required && (
            <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span>
          )}
        </label>
        {optional && (
          <span style={{ fontSize: 10, color: "#64748b", fontWeight: 500 }}>
            Optional
          </span>
        )}
      </div>
      {(displayId || displaySub || evidence) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 2,
            marginBottom: 4,
            flexWrap: "wrap",
          }}
        >
          {displayId && (
            <span
              style={{
                fontSize: 9,
                fontFamily: "monospace",
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 5,
                background: "#f1f5f9",
                color: "#1e293b",
                border: "1px solid rgba(99,102,241,0.1)",
              }}
            >
              {displayId}
            </span>
          )}
          {evidence && <SuggestedEvidenceTrigger evidence={evidence} />}
          {displaySub && (
            <span style={{ fontSize: 10, color: "#1e293b" }}>{displaySub}</span>
          )}
        </div>
      )}
    </div>
  );
}

function AttachmentUpload({ fieldId, required }) {
  const [file, setFile] = useState(null);
  return (
    <div
      style={{
        marginTop: 8,
        padding: "10px 12px",
        borderRadius: 10,
        border: `1.5px dashed ${required ? "#f59e0b" : "rgba(99,102,241,0.25)"}`,
        background: required ? "#fffbeb" : "#f8fafc",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 6,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: required ? "#92400e" : "#64748b",
            flex: 1,
          }}
        >
          {required ? "Attachment Required *" : "Attachment (optional)"}
        </div>
      </div>
      <input
        type="file"
        id={`attach-${fieldId}`}
        style={{ display: "none" }}
        onChange={(e) => setFile(e.target.files?.[0] || null)}
      />
      <label
        htmlFor={`attach-${fieldId}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 12px",
          borderRadius: 8,
          cursor: "pointer",
          fontSize: 12,
          background: "#fff",
          border: "1px solid rgba(99,102,241,0.2)",
          color: "#4338ca",
          fontWeight: 500,
        }}
      >
        📎 {file ? file.name : "Choose file"}
      </label>
      {file && (
        <span style={{ marginLeft: 8, fontSize: 11, color: "#64748b" }}>
          {(file.size / 1024).toFixed(1)} KB
        </span>
      )}
    </div>
  );
}

function FInput({
  label,
  id,
  placeholder,
  required,
  optional,
  value,
  onChange,
  type = "text",
  icon: IconComp,
  hint,
  error,
  fieldId,
  subsection,
}) {
  const [f, setF] = useState(false);
  const schemaField =
    id && window.__piaSchemaFields
      ? window.__piaSchemaFields.find((x) => x.external_id === id)
      : null;
  const allowAttachment = schemaField?.allow_attachment;
  const attachmentRequired = schemaField?.attachment_required;
  const referenceEvidence =
    (typeof window !== "undefined" && window.__piaReferenceEvidence?.[id]) || null;

  return (
    <div
      data-field-wrapper
      data-field-id={id}
      id={id ? `wrap-${id}` : undefined}
    >
      {label && (
        <FL
          required={required}
          optional={optional}
          fieldId={fieldId}
          subsection={subsection}
        >
          {label}
        </FL>
      )}
      {hint && (
        <p style={{ fontSize: 11, color: "#475569", marginBottom: 6 }}>
          {hint}
        </p>
      )}
      <div style={{ position: "relative" }}>
        {IconComp && (
          <div
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: f ? "#6366f1" : "#64748b",
              pointerEvents: "none",
              display: "flex",
            }}
          >
            <IconComp size={13} />
          </div>
        )}
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setF(true)}
          onBlur={() => setF(false)}
          placeholder={placeholder}
          style={{ ...fld(f, error), paddingLeft: IconComp ? 32 : 12 }}
        />
      </div>
      {error && (
        <p style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>{error}</p>
      )}
      {allowAttachment && (
        <AttachmentUpload
          fieldId={id}
          required={attachmentRequired}
          referenceEvidence={referenceEvidence}
        />
      )}
    </div>
  );
}

function FTextarea({
  label,
  id,
  placeholder,
  required,
  optional,
  value,
  onChange,
  rows = 3,
  error,
  fieldId,
  subsection,
}) {
  const [f, setF] = useState(false);
  const len = value?.length || 0;
  const schemaField =
    id && window.__piaSchemaFields
      ? window.__piaSchemaFields.find((x) => x.external_id === id)
      : null;
  const allowAttachment = schemaField?.allow_attachment;
  const attachmentRequired = schemaField?.attachment_required;
  const referenceEvidence =
    (typeof window !== "undefined" && window.__piaReferenceEvidence?.[id]) || null;

  return (
    <div
      data-field-wrapper
      data-field-id={id}
      id={id ? `wrap-${id}` : undefined}
    >
      {label && (
        <FL
          required={required}
          optional={optional}
          fieldId={fieldId || id}
          subsection={subsection}
        >
          {label}
        </FL>
      )}
      <textarea
        id={id}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        placeholder={placeholder}
        style={{ ...fld(f, error), resize: "vertical" }}
      />
      {error && (
        <p style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>{error}</p>
      )}
      {len > 20 && (
        <div
          style={{
            textAlign: "right",
            fontSize: 10,
            color: "#64748b",
            marginTop: 3,
          }}
        >
          {len} chars
        </div>
      )}
      {allowAttachment && (
        <AttachmentUpload
          fieldId={id}
          required={attachmentRequired}
          referenceEvidence={referenceEvidence}
        />
      )}
    </div>
  );
}

function FSelect({
  label,
  id,
  options,
  required,
  optional,
  value,
  onChange,
  icon: IconComp,
  placeholder = "Select an option…",
  error,
  fieldId,
  subsection,
}) {
  const [f, setF] = useState(false);
  const schemaField =
    id && window.__piaSchemaFields
      ? window.__piaSchemaFields.find((x) => x.external_id === id)
      : null;
  const allowAttachment = schemaField?.allow_attachment;
  const attachmentRequired = schemaField?.attachment_required;
  const referenceEvidence =
    (typeof window !== "undefined" && window.__piaReferenceEvidence?.[id]) || null;

  return (
    <div
      data-field-wrapper
      data-field-id={id}
      id={id ? `wrap-${id}` : undefined}
    >
      {label && (
        <FL
          required={required}
          optional={optional}
          fieldId={fieldId || id}
          subsection={subsection}
        >
          {label}
        </FL>
      )}
      <div style={{ position: "relative" }}>
        {IconComp && (
          <div
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: f ? "#6366f1" : "#64748b",
              pointerEvents: "none",
              zIndex: 1,
              display: "flex",
            }}
          >
            <IconComp size={13} />
          </div>
        )}
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setF(true)}
          onBlur={() => setF(false)}
          style={{
            ...fld(f, error),
            paddingLeft: IconComp ? 32 : 12,
            paddingRight: 32,
            appearance: "none",
            cursor: "pointer",
            color: value ? "#0f172a" : "#64748b",
          }}
        >
          <option value="" style={{ color: "#64748b" }}>
            {placeholder}
          </option>
          {options.map((o) => (
            <option key={o} value={o} style={{ color: "#0f172a" }}>
              {o}
            </option>
          ))}
        </select>
        <div
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#475569",
            pointerEvents: "none",
            display: "flex",
          }}
        >
          <ChevronDown size={13} />
        </div>
      </div>
      {error && (
        <p style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>{error}</p>
      )}
      {allowAttachment && (
        <AttachmentUpload
          fieldId={id}
          required={attachmentRequired}
          referenceEvidence={referenceEvidence}
        />
      )}
    </div>
  );
}

function FMultiSelect({
  label,
  id,
  options,
  required,
  optional,
  value = [],
  onChange,
  error,
  fieldId,
  subsection,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef();
  const schemaField =
    id && window.__piaSchemaFields
      ? window.__piaSchemaFields.find((x) => x.external_id === id)
      : null;
  const allowAttachment = schemaField?.allow_attachment;
  const attachmentRequired = schemaField?.attachment_required;
  const referenceEvidence =
    (typeof window !== "undefined" && window.__piaReferenceEvidence?.[id]) || null;

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle = (o) =>
    value.includes(o)
      ? onChange(value.filter((v) => v !== o))
      : onChange([...value, o]);
  const filtered = options.filter((o) =>
    o.toLowerCase().includes(q.toLowerCase()),
  );
  return (
    <div
      ref={ref}
      data-field-wrapper
      data-field-id={id}
      id={id ? `wrap-${id}` : undefined}
    >
      {label && (
        <FL
          required={required}
          optional={optional}
          fieldId={fieldId || id}
          subsection={subsection}
        >
          {label}
          {value.length > 0 && (
            <span
              style={{
                marginLeft: 6,
                fontSize: 10,
                fontWeight: 700,
                background: "#6366f1",
                color: "#fff",
                borderRadius: "50%",
                width: 16,
                height: 16,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {value.length}
            </span>
          )}
        </FL>
      )}
      <div
        id={id}
        onClick={() => setOpen(!open)}
        style={{
          ...fld(open, error),
          padding: "8px 10px",
          cursor: "pointer",
          minHeight: 40,
          display: "flex",
          alignItems: "flex-start",
          gap: 6,
        }}
      >
        <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 5 }}>
          {value.length === 0 && (
            <span
              style={{ fontSize: 12.5, color: "#64748b", lineHeight: "22px" }}
            >
              Select options…
            </span>
          )}
          {value.map((v) => (
            <span
              key={v}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "#eef2ff",
                border: "1px solid rgba(99,102,241,0.3)",
                color: "#4338ca",
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 6,
              }}
            >
              {v}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(v);
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#818cf8",
                  lineHeight: 1,
                  padding: 0,
                  display: "flex",
                }}
              >
                <X size={9} />
              </button>
            </span>
          ))}
        </div>
        <ChevronDown
          size={13}
          color="#475569"
          style={{
            flexShrink: 0,
            marginTop: 4,
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </div>
      {open && (
        <div
          style={{
            marginTop: 4,
            background: "#ffffff",
            border: "1.5px solid rgba(99,102,241,0.25)",
            borderRadius: 12,
            boxShadow:
              "0 8px 24px rgba(99,102,241,0.12),0 2px 8px rgba(0,0,0,0.06)",
            overflow: "hidden",
            zIndex: 50,
          }}
        >
          <div
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid rgba(99,102,241,0.1)",
            }}
          >
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              style={{
                width: "100%",
                fontSize: 11.5,
                padding: "6px 10px",
                borderRadius: 8,
                background: "#f8fafc",
                border: "1px solid rgba(99,102,241,0.15)",
                outline: "none",
                fontFamily: "inherit",
                color: "#0f172a",
              }}
            />
          </div>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filtered.map((opt) => {
              const sel = value.includes(opt);
              return (
                <button
                  key={opt}
                  onClick={() => toggle(opt)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 12px",
                    background: sel ? "#eef2ff" : "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: 12.5,
                    color: sel ? "#4338ca" : "#1e293b",
                    transition: "background 0.1s",
                    fontFamily: "inherit",
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 5,
                      border: `2px solid ${sel ? "#6366f1" : "#d1d5db"}`,
                      background: sel ? "#6366f1" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {sel && <Check size={9} color="#fff" />}
                  </div>
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {allowAttachment && (
        <AttachmentUpload
          fieldId={id}
          required={attachmentRequired}
          referenceEvidence={referenceEvidence}
        />
      )}
    </div>
  );
}

function FRadioGroup({
  label,
  id,
  options,
  required,
  optional,
  value,
  onChange,
  error,
  fieldId,
  subsection,
}) {
  const schemaField =
    id && window.__piaSchemaFields
      ? window.__piaSchemaFields.find((x) => x.external_id === id)
      : null;
  const allowAttachment = schemaField?.allow_attachment;
  const attachmentRequired = schemaField?.attachment_required;
  const referenceEvidence =
    (typeof window !== "undefined" && window.__piaReferenceEvidence?.[id]) || null;

  return (
    <div
      data-field-wrapper
      data-field-id={id}
      id={id ? `wrap-${id}` : undefined}
    >
      {label && (
        <FL
          required={required}
          optional={optional}
          fieldId={fieldId || id}
          subsection={subsection}
        >
          {label}
        </FL>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {options.map((o) => {
          const sel = value === o;
          return (
            <label
              key={o}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 12px",
                borderRadius: 10,
                border: `1.5px solid ${sel ? "#6366f1" : "rgba(99,102,241,0.2)"}`,
                background: sel ? "#eef2ff" : "#fff",
                cursor: "pointer",
                fontSize: 12.5,
                color: sel ? "#4338ca" : "#334155",
                fontWeight: sel ? 600 : 500,
                transition: "all 0.15s",
              }}
            >
              <input
                type="radio"
                name={id}
                checked={sel}
                onChange={() => onChange(o)}
                style={{ width: 15, height: 15, accentColor: "#6366f1" }}
              />
              {o}
            </label>
          );
        })}
      </div>
      {error && (
        <p style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>{error}</p>
      )}
      {allowAttachment && (
        <AttachmentUpload
          fieldId={id}
          required={attachmentRequired}
          referenceEvidence={referenceEvidence}
        />
      )}
    </div>
  );
}

function FCheckboxGroup({
  label,
  id,
  options,
  required,
  optional,
  value = [],
  onChange,
  error,
  fieldId,
  subsection,
}) {
  const toggle = (o) =>
    value.includes(o)
      ? onChange(value.filter((v) => v !== o))
      : onChange([...value, o]);
  const schemaField =
    id && window.__piaSchemaFields
      ? window.__piaSchemaFields.find((x) => x.external_id === id)
      : null;
  const allowAttachment = schemaField?.allow_attachment;
  const attachmentRequired = schemaField?.attachment_required;
  const referenceEvidence =
    (typeof window !== "undefined" && window.__piaReferenceEvidence?.[id]) || null;

  return (
    <div
      data-field-wrapper
      data-field-id={id}
      id={id ? `wrap-${id}` : undefined}
    >
      {label && (
        <FL
          required={required}
          optional={optional}
          fieldId={fieldId || id}
          subsection={subsection}
        >
          {label}
          {value.length > 0 && (
            <span
              style={{
                marginLeft: 6,
                fontSize: 10,
                fontWeight: 700,
                background: "#6366f1",
                color: "#fff",
                borderRadius: "50%",
                width: 16,
                height: 16,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {value.length}
            </span>
          )}
        </FL>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {options.map((o) => {
          const sel = value.includes(o);
          return (
            <label
              key={o}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 12px",
                borderRadius: 10,
                border: `1.5px solid ${sel ? "#6366f1" : "rgba(99,102,241,0.2)"}`,
                background: sel ? "#eef2ff" : "#fff",
                cursor: "pointer",
                fontSize: 12.5,
                color: sel ? "#4338ca" : "#334155",
                fontWeight: sel ? 600 : 500,
                transition: "all 0.15s",
              }}
            >
              <input
                type="checkbox"
                checked={sel}
                onChange={() => toggle(o)}
                style={{ width: 15, height: 15, accentColor: "#6366f1" }}
              />
              {o}
            </label>
          );
        })}
      </div>
      {error && (
        <p style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>{error}</p>
      )}
      {allowAttachment && (
        <AttachmentUpload
          fieldId={id}
          required={attachmentRequired}
          referenceEvidence={referenceEvidence}
        />
      )}
    </div>
  );
}

function FTagInput({
  label,
  id,
  placeholder,
  required,
  optional,
  value = [],
  onChange,
  error,
}) {
  const [input, setInput] = useState("");
  const add = () => {
    const t = input.trim();
    if (t && !value.includes(t)) {
      onChange([...value, t]);
      setInput("");
    }
  };
  return (
    <div
      data-field-wrapper
      data-field-id={id}
      id={id ? `wrap-${id}` : undefined}
    >
      {label && (
        <FL required={required} optional={optional} fieldId={id}>
          {label}
        </FL>
      )}
      <div
        id={id}
        style={{
          background: "#ffffff",
          border: error
            ? "1px solid #fca5a5"
            : "1.5px solid rgba(99,102,241,0.2)",
          borderRadius: 10,
          padding: "8px 10px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          transition: "border-color 0.15s",
        }}
        onFocusCapture={(e) => (e.currentTarget.style.borderColor = "#6366f1")}
        onBlurCapture={(e) =>
          (e.currentTarget.style.borderColor = "rgba(99,102,241,0.2)")
        }
      >
        {value.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 5,
              marginBottom: 8,
            }}
          >
            {value.map((tag) => (
              <span
                key={tag}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  color: "#1e293b",
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "3px 9px",
                  borderRadius: 6,
                }}
              >
                {tag}
                <button
                  onClick={() => onChange(value.filter((t) => t !== tag))}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#64748b",
                    padding: 0,
                    display: "flex",
                  }}
                >
                  <X size={9} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            id={id}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
            placeholder={value.length === 0 ? placeholder : "Add another…"}
            style={{
              flex: 1,
              fontSize: 12.5,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "#0f172a",
              fontFamily: "inherit",
            }}
          />
          {input.trim() && (
            <button
              onClick={add}
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                background: "#6366f1",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Plus size={11} color="#fff" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FRange({
  label,
  id,
  min = 1,
  max = 5,
  value,
  onChange,
  required,
  color = "#6366f1",
}) {
  const pct = ((value - min) / (max - min)) * 100;
  const meta = LEVEL_META[value] || LEVEL_META[3];
  return (
    <div>
      {label && <FL required={required} fieldId={id}>{label}</FL>}
      <div
        style={{
          background: "#ffffff",
          border: "1.5px solid rgba(99,102,241,0.18)",
          borderRadius: 12,
          padding: "14px 16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: meta.color,
                lineHeight: 1,
              }}
            >
              {value}
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: "3px 8px",
                borderRadius: 20,
                color: meta.color,
                background: meta.color + "18",
                border: `1px solid ${meta.color}30`,
              }}
            >
              {meta.label}
            </span>
          </div>
          <span style={{ fontSize: 10, color: "#64748b" }}>out of {max}</span>
        </div>
        <div
          style={{
            position: "relative",
            height: 6,
            borderRadius: 9,
            background: "#f1f5f9",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              left: 0,
              width: `${pct}%`,
              height: "100%",
              borderRadius: 9,
              background: `linear-gradient(90deg, ${color}80, ${color})`,
              transition: "width 0.3s ease",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: "#ffffff",
              border: `2.5px solid ${meta.color}`,
              boxShadow: `0 2px 8px ${meta.color}40`,
              transition: "left 0.3s ease",
              left: `calc(${pct}% - 9px)`,
            }}
          />
          <input
            id={id}
            type="range"
            min={min}
            max={max}
            value={value}
            onChange={(e) => onChange(+e.target.value)}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              opacity: 0,
              cursor: "pointer",
            }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {Array.from({ length: max - min + 1 }, (_, i) => i + min).map((n) => (
            <button
              key={n}
              onClick={() => onChange(n)}
              style={{
                width: 28,
                height: 24,
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
                background: value === n ? meta.color : "transparent",
                color: value === n ? "#fff" : "#64748b",
                transition: "all 0.15s",
                fontFamily: "inherit",
              }}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Grid helpers ─────────────────────────────────────────────────────────────
const G2 = ({ children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
      gap: 16,
    }}
  >
    {children}
  </div>
);

// ─── Field group card ─────────────────────────────────────────────────────────
function FieldGroup({
  title,
  subtitle,
  icon: Ico,
  color = "#6366f1",
  children,
}) {
  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 16,
        border: "1px solid rgba(99,102,241,0.14)",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        marginBottom: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 18px",
          background: `linear-gradient(135deg, ${color}0a, ${color}04)`,
          borderBottom: `1px solid ${color}14`,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `linear-gradient(135deg, ${color}30, ${color}18)`,
            border: `1.5px solid ${color}35`,
            boxShadow: `0 2px 6px ${color}25`,
            flexShrink: 0,
          }}
        >
          {Ico && <Ico size={15} color={color} />}
        </div>
        <div>
          <p
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: "#0f172a",
              lineHeight: 1.2,
            }}
          >
            {title}
          </p>
          {subtitle && (
            <p style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <div
        style={{
          padding: "18px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Info banner ──────────────────────────────────────────────────────────────
function InfoBanner({ children, variant = "blue" }) {
  const cfg = {
    blue: { bg: "#eff6ff", border: "rgba(99,102,241,0.22)", color: "#1e40af" },
    amber: { bg: "#fffbeb", border: "rgba(251,191,36,0.35)", color: "#92400e" },
  }[variant];
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        padding: "11px 14px",
        borderRadius: 10,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        fontSize: 12,
        color: cfg.color,
        lineHeight: 1.55,
      }}
    >
      <div
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: cfg.color,
          flexShrink: 0,
          marginTop: 5,
        }}
      />
      <span>{children}</span>
    </div>
  );
}

// ─── Validation ───────────────────────────────────────────────────────────────
function isSchemaFieldFilled(field, value) {
  if (value === false || value === 0) return true;
  if (["multiselect", "checkbox_multi"].includes(field.field_type)) {
    return Array.isArray(value) && value.length > 0;
  }
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function validate(stepIdx, data, schemaFields = []) {
  const stepId = STEPS[stepIdx].id;
  const d = data[stepId] || {};
  const e = {};

  if (schemaFields.length > 0) {
    schemaFields
      .filter(
        (f) =>
          f.section_id === stepId &&
          f.is_active !== false &&
          isFieldRequired(f.required, true),
      )
      .forEach((field) => {
        if (!isSchemaFieldFilled(field, d[field.external_id])) {
          e[field.external_id] = `${field.text || "This field"} is required`;
        }
      });
    return e;
  }

  if (stepIdx === 0) {
    if (!d.vendor) e.vendor = "Vendor is required";
    if (!d.projectName?.trim()) e.projectName = "Project name is required";
    if (!d.ownerDept?.trim()) e.ownerDept = "Owner department is required";
    if (!d.procDesc?.trim()) e.procDesc = "Processing description is required";
    if (!d.bizPurpose?.trim()) e.bizPurpose = "Business purpose is required";
    if (!d.legalBasis?.length) e.legalBasis = "Select at least one legal basis";
  }
  if (stepIdx === 1) {
    if (!d.dataTypes?.length) e.dataTypes = "Select at least one data type";
    if (!d.sensitiveData)
      e.sensitiveData = "Indicate if sensitive data is involved";
    if (!d.dataSubjects?.length)
      e.dataSubjects = "Add at least one data subject";
  }
  if (stepIdx === 2) {
    if (!d.storageLocations?.length)
      e.storageLocations = "Select at least one storage location";
    if (!d.collectionMethod)
      e.collectionMethod = "Select a data collection method";
    if (!d.internalTransfers?.trim())
      e.internalTransfers = "Describe internal transfers";
    if (!d.externalTransfers?.trim())
      e.externalTransfers = "Describe external transfers";
  }
  if (stepIdx === 3) {
    if (!d.mitigation?.trim())
      e.mitigation = "Mitigation measures are required";
  }
  if (stepIdx === 4) {
    if (!d.controlTypes) e.controlTypes = "Select a control type";
    if (!d.complianceStatus) e.complianceStatus = "Select compliance status";
    if (!d.dataLocations?.trim())
      e.dataLocations = "Data locations are required";
  }
  if (stepIdx === 5) {
    if (!d.gdprSafeguards?.trim())
      e.gdprSafeguards = "GDPR safeguards are required";
    if (!d.dpdpSafeguards?.trim())
      e.dpdpSafeguards = "DPDP safeguards are required";
    if (!d.transferJustification?.trim())
      e.transferJustification = "Transfer justification is required";
  }
  return e;
}

// ─── Schema lookup helpers ────────────────────────────────────────────────────

/**
 * Find a field definition in the tenant schema by its external_id.
 * Returns null if the schema hasn't loaded yet or the field isn't present.
 */
function schemaField(schemaFields, externalId, sectionId = null) {
  if (sectionId) {
    return (
      schemaFields.find(
        (f) => f.section_id === sectionId && f.external_id === externalId,
      ) ||
      schemaFields.find((f) => f.external_id === externalId) ||
      null
    );
  }
  return schemaFields.find((f) => f.external_id === externalId) || null;
}

function normalizePiaSchemaField(field) {
  return {
    ...field,
    required: field.required === false ? false : true,
    order: Number(field.order) || 0,
  };
}

function normalizePiaSchemaFields(fields) {
  return (fields || []).map(normalizePiaSchemaField);
}

/**
 * Extract a clean string[] of option labels from a schema field.
 * Works for both string[] and {label, weight}[] option formats.
 */
function schemaOpts(field) {
  return (field?.options || []).map((o) =>
    typeof o === "string" ? o : o.label || "",
  );
}

/**
 * Render any schema field whose field_type may have been changed by the tenant.
 * Used as a fallback when a hardcoded field has been switched to a different type.
 */
function DynamicField({ field, d, set, errors }) {
  const id = field.external_id;
  const label = field.text;
  const required = isFieldRequired(field.required, true);
  const opts = schemaOpts(field);
  const val =
    d[id] ??
    (["multiselect", "checkbox_multi"].includes(field.field_type) ? [] : "");
  const error = errors[id];

  switch (field.field_type) {
    case "select":
    case "boolean":
      return (
        <FSelect
          label={label}
          id={id}
          required={required}
          optional={!required}
          options={opts}
          value={val}
          onChange={set(id)}
          error={error}
        />
      );
    case "checkbox_single":
      return (
        <FRadioGroup
          label={label}
          id={id}
          required={required}
          optional={!required}
          options={opts}
          value={val}
          onChange={set(id)}
          error={error}
        />
      );
    case "checkbox_multi":
      return (
        <FCheckboxGroup
          label={label}
          id={id}
          required={required}
          optional={!required}
          options={opts}
          value={Array.isArray(val) ? val : []}
          onChange={set(id)}
          error={error}
        />
      );
    case "multiselect":
      return (
        <FMultiSelect
          label={label}
          id={id}
          required={required}
          optional={!required}
          options={opts}
          value={Array.isArray(val) ? val : []}
          onChange={set(id)}
          error={error}
        />
      );
    case "textarea":
      return (
        <FTextarea
          label={label}
          id={id}
          required={required}
          optional={!required}
          value={val}
          onChange={set(id)}
          error={error}
        />
      );
    case "date":
      return (
        <FInput
          label={label}
          id={id}
          type="date"
          required={required}
          optional={!required}
          value={val}
          onChange={set(id)}
          error={error}
        />
      );
    default: // "text" and anything else
      return (
        <FInput
          label={label}
          id={id}
          required={required}
          optional={!required}
          value={val}
          onChange={set(id)}
          error={error}
        />
      );
  }
}

// Renders any tenant-added custom fields not already hardcoded in the panel
function SchemaExtraFields({ schemaFields = [], sectionId, d, set, errors }) {
  const HARDCODED_IDS = {
    general: [
      "vendor",
      "projectName",
      "ownerDept",
      "procDesc",
      "bizPurpose",
      "legalBasis",
      "duration",
    ],
    dataDetails: ["dataTypes", "sensitiveData", "dataSubjects", "volume"],
    dataFlow: [
      "storageLocations",
      "collectionMethod",
      "internalTransfers",
      "externalTransfers",
    ],
    risk: ["likelihood", "impact", "mitigation"],
    vendorControls: [
      "controlTypes",
      "complianceStatus",
      "certifications",
      "subProcessors",
      "dataLocations",
      "vendorContact",
      "contractExpiry",
    ],
    safeguards: [
      "gdprSafeguards",
      "dpdpSafeguards",
      "transferJustification",
      "reviewerName",
      "reviewDate",
      "acknowledged",
    ],
  };
  const knownIds = new Set(HARDCODED_IDS[sectionId] || []);
  const extras = schemaFields.filter(
    (f) => !knownIds.has(f.external_id) && f.is_active,
  );
  if (!extras.length) return null;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        marginTop: 4,
      }}
    >
      {extras.map((f) => {
        const val = d[f.external_id] ?? "";
        const opts = (f.options || []).map((o) =>
          typeof o === "string" ? o : o.label,
        );
        const commonProps = {
          label: f.text,
          id: f.external_id,
          required: f.required,
          value: val,
          onChange: set(f.external_id),
          error: errors[f.external_id],
          fieldId: f.external_id,
          subsection: f.config?.dpdp_ref,
        };

        let fieldEl = null;

        if (f.field_type === "checkbox_single") {
          fieldEl = <FRadioGroup {...commonProps} options={opts} />;
        } else if (f.field_type === "checkbox_multi") {
          fieldEl = (
            <FCheckboxGroup
              {...commonProps}
              options={opts}
              value={Array.isArray(val) ? val : []}
            />
          );
        } else if (f.field_type === "select" || f.field_type === "boolean") {
          fieldEl = <FSelect {...commonProps} options={opts} />;
        } else if (f.field_type === "multiselect") {
          fieldEl = (
            <FMultiSelect
              {...commonProps}
              options={opts}
              value={Array.isArray(val) ? val : []}
            />
          );
        } else if (f.field_type === "textarea") {
          fieldEl = <FTextarea {...commonProps} rows={3} />;
        } else {
          fieldEl = <FInput {...commonProps} />;
        }

        return (
          <div key={f.external_id}>
            {fieldEl}
            {f.allow_attachment && (
              <AttachmentUpload
                fieldId={f.external_id}
                required={f.attachment_required}
                referenceEvidence={
                  (typeof window !== "undefined" &&
                    window.__piaReferenceEvidence?.[f.external_id]) ||
                  null
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
// ─── Step panels ──────────────────────────────────────────────────────────────
function GeneralPanel({ d, set, errors, accent, schemaFields = [] }) {
  const sf = (id) => schemaField(schemaFields, id, "general");

  const vendorSF = sf("vendor");
  const vendorOpts = schemaOpts(vendorSF).length
    ? schemaOpts(vendorSF)
    : ["Vendor A", "Vendor B", "Vendor C", "Internal"];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <FieldGroup
        title="Project Identification"
        subtitle="Core project and organisational details"
        icon={FileText}
        color={accent}
      >
        <G2>
          {/* vendor — respect schema field_type change */}
          {vendorSF && vendorSF.field_type !== "select" ? (
            <DynamicField field={vendorSF} d={d} set={set} errors={errors} />
          ) : (
            <FSelect
              label={vendorSF?.text || "Vendor"}
              id="vendor"
              required={vendorSF ? vendorSF.required : true}
              icon={Building2}
              options={vendorOpts}
              value={d.vendor || ""}
              onChange={set("vendor")}
              error={errors.vendor}
            />
          )}
          {/* projectName */}
          {sf("projectName") && sf("projectName").field_type !== "text" ? (
            <DynamicField
              field={sf("projectName")}
              d={d}
              set={set}
              errors={errors}
            />
          ) : (
            <FInput
              label={sf("projectName")?.text || "Project Name"}
              id="projectName"
              required={sf("projectName") ? sf("projectName").required : true}
              icon={FileText}
              placeholder="e.g., Customer Data Analytics Platform"
              value={d.projectName || ""}
              onChange={set("projectName")}
              error={errors.projectName}
            />
          )}
        </G2>
        {/* ownerDept */}
        {sf("ownerDept") && sf("ownerDept").field_type !== "text" ? (
          <DynamicField
            field={sf("ownerDept")}
            d={d}
            set={set}
            errors={errors}
          />
        ) : (
          <FInput
            label={sf("ownerDept")?.text || "Owner Department"}
            id="ownerDept"
            required={sf("ownerDept") ? sf("ownerDept").required : true}
            icon={Building2}
            placeholder="e.g., Engineering, Legal, Product"
            value={d.ownerDept || ""}
            onChange={set("ownerDept")}
            error={errors.ownerDept}
          />
        )}
        {(errors.vendor || errors.projectName || errors.ownerDept) && (
          <ErrBanner
            errors={[
              errors.vendor,
              errors.projectName,
              errors.ownerDept,
            ].filter(Boolean)}
          />
        )}
      </FieldGroup>

      <FieldGroup
        title="Processing Context"
        subtitle="Describe the nature and purpose of data processing"
        icon={Lock}
        color="#7c3aed"
      >
        {sf("procDesc") && sf("procDesc").field_type !== "textarea" ? (
          <DynamicField
            field={sf("procDesc")}
            d={d}
            set={set}
            errors={errors}
          />
        ) : (
          <FTextarea
            label={sf("procDesc")?.text || "Processing Description"}
            id="procDesc"
            required={sf("procDesc") ? sf("procDesc").required : true}
            rows={4}
            placeholder="Describe the nature, scope, context, and purpose of the processing."
            value={d.procDesc || ""}
            onChange={set("procDesc")}
            error={errors.procDesc}
          />
        )}
        {sf("bizPurpose") && sf("bizPurpose").field_type !== "textarea" ? (
          <DynamicField
            field={sf("bizPurpose")}
            d={d}
            set={set}
            errors={errors}
          />
        ) : (
          <FTextarea
            label={sf("bizPurpose")?.text || "Business Purpose"}
            id="bizPurpose"
            required={sf("bizPurpose") ? sf("bizPurpose").required : true}
            rows={3}
            placeholder="Explain the legitimate business rationale."
            value={d.bizPurpose || ""}
            onChange={set("bizPurpose")}
            error={errors.bizPurpose}
          />
        )}
        {(errors.procDesc || errors.bizPurpose) && (
          <ErrBanner
            errors={[errors.procDesc, errors.bizPurpose].filter(Boolean)}
          />
        )}
      </FieldGroup>

      <FieldGroup
        title="Legal Framework"
        subtitle="Select one or more lawful bases under which processing occurs"
        icon={Scale}
        color="#4338ca"
      >
        {/* legalBasis — keep card picker UI, but if tenant changes to select/text use DynamicField */}
        {sf("legalBasis") &&
        !["", "select", undefined].includes(sf("legalBasis")?.field_type) &&
        sf("legalBasis").field_type !== "select" ? (
          <DynamicField
            field={sf("legalBasis")}
            d={d}
            set={set}
            errors={errors}
          />
        ) : (
          <div id="legalBasis" data-field-wrapper data-field-id="legalBasis">
            <FL
              required={sf("legalBasis") ? sf("legalBasis").required : true}
              fieldId="legalBasis"
            >
              {sf("legalBasis")?.text || "Legal Basis for Processing"}
            </FL>
            {errors.legalBasis && <ErrBanner errors={[errors.legalBasis]} />}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 8,
              }}
            >
              {LEGAL_BASIS.map((b) => {
                const selected = Array.isArray(d.legalBasis)
                  ? d.legalBasis
                  : d.legalBasis
                    ? [d.legalBasis]
                    : [];
                const sel = selected.includes(b.id);
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => {
                      const next = sel
                        ? selected.filter((id) => id !== b.id)
                        : [...selected, b.id];
                      set("legalBasis")(next);
                    }}
                    style={{
                      boxSizing: "border-box",
                      width: 168,
                      height: 52,
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 10,
                      border: `1px solid ${sel ? "#6366f1" : "rgba(99,102,241,0.15)"}`,
                      background: sel
                        ? "linear-gradient(135deg,#eef2ff,#e0e7ff)"
                        : "#ffffff",
                      cursor: "pointer",
                      textAlign: "left",
                      boxShadow: sel
                        ? "0 0 0 2px rgba(99,102,241,0.12)"
                        : "0 1px 2px rgba(0,0,0,0.04)",
                      transition: "all 0.15s",
                      fontFamily: "inherit",
                    }}
                  >
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        background: sel
                          ? "linear-gradient(135deg,#6366f1,#818cf8)"
                          : "linear-gradient(135deg,#f1f5f9,#e8eaf6)",
                      }}
                    >
                      <b.Icon size={11} color={sel ? "#fff" : "#334155"} />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p
                        title={b.label}
                        style={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          color: sel ? "#4338ca" : "#1e293b",
                          lineHeight: 1.15,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {b.label}
                      </p>
                      <p
                        title={b.desc}
                        style={{
                          fontSize: 8.5,
                          color: "#64748b",
                          lineHeight: 1.15,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {b.desc}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            {sf("legalBasis")?.allow_attachment && (
              <AttachmentUpload
                fieldId="legalBasis"
                required={sf("legalBasis").attachment_required}
                referenceEvidence={
                  (typeof window !== "undefined" &&
                    window.__piaReferenceEvidence?.legalBasis) ||
                  null
                }
              />
            )}
          </div>
        )}
        <FInput
          label="Processing Duration"
          id="duration"
          optional
          icon={Clock}
          placeholder="e.g., 6 months, 2 years, ongoing"
          value={d.duration || ""}
          onChange={set("duration")}
        />
      </FieldGroup>

      <SchemaExtraFields
        schemaFields={schemaFields}
        sectionId="general"
        d={d}
        set={set}
        errors={errors}
      />
    </div>
  );
}
function DataDetailsPanel({ d, set, errors, accent, schemaFields = [] }) {
  const sf = (id) => schemaField(schemaFields, id, "dataDetails");

  const dataTypesSF = sf("dataTypes");
  const dataTypesOpts = schemaOpts(dataTypesSF).length
    ? schemaOpts(dataTypesSF)
    : DATA_TYPES;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <FieldGroup
        title="Data Classification"
        subtitle="Specify the categories of personal data being processed"
        icon={Shield}
        color={accent}
      >
        {/* dataTypes — keep multiselect UI but use schema options/label */}
        {dataTypesSF && dataTypesSF.field_type !== "multiselect" ? (
          <DynamicField field={dataTypesSF} d={d} set={set} errors={errors} />
        ) : (
          <FMultiSelect
            label={dataTypesSF?.text || "Personal Data Types"}
            id="dataTypes"
            required={dataTypesSF ? dataTypesSF.required : true}
            options={dataTypesOpts}
            value={d.dataTypes || []}
            onChange={set("dataTypes")}
            error={errors.dataTypes}
          />
        )}
        {errors.dataTypes && <ErrBanner errors={[errors.dataTypes]} />}

        {/* sensitiveData — keep Yes/No card UI; if type changed use DynamicField */}
        {sf("sensitiveData") && sf("sensitiveData").field_type !== "boolean" ? (
          <DynamicField
            field={sf("sensitiveData")}
            d={d}
            set={set}
            errors={errors}
          />
        ) : (
          <div>
            <FL
              required={
                sf("sensitiveData") ? sf("sensitiveData").required : true
              }
              fieldId="sensitiveData"
            >
              {sf("sensitiveData")?.text ||
                "Involves Special Category / Sensitive Data?"}
            </FL>
            <div
              id="sensitiveData"
              data-field-wrapper
              data-field-id="sensitiveData"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              {[
                {
                  val: "Yes",
                  label: "Yes, includes sensitive data",
                  Icon: Zap,
                  color: "#ef4444",
                  bg: "#fff1f2",
                  border: "#fca5a5",
                },
                {
                  val: "No",
                  label: "No, standard personal data only",
                  Icon: Check,
                  color: "#10b981",
                  bg: "#f0fdf4",
                  border: "#86efac",
                },
              ].map((opt) => {
                const sel = d.sensitiveData === opt.val;
                return (
                  <button
                    key={opt.val}
                    onClick={() => set("sensitiveData")(opt.val)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: `1.5px solid ${sel ? opt.border : "rgba(99,102,241,0.15)"}`,
                      background: sel ? opt.bg : "#ffffff",
                      cursor: "pointer",
                      textAlign: "left",
                      boxShadow: sel ? `0 0 0 2px ${opt.color}18` : "none",
                      transition: "all 0.15s",
                      fontFamily: "inherit",
                    }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        background: sel
                          ? opt.color
                          : "linear-gradient(135deg,#f1f5f9,#e8eaf6)",
                        boxShadow: sel ? `0 2px 8px ${opt.color}40` : "none",
                      }}
                    >
                      <opt.Icon size={15} color={sel ? "#fff" : "#334155"} />
                    </div>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: sel ? opt.color : "#1e293b",
                      }}
                    >
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {errors.sensitiveData && (
              <ErrBanner errors={[errors.sensitiveData]} />
            )}
            {sf("sensitiveData")?.allow_attachment && (
              <AttachmentUpload
                fieldId="sensitiveData"
                required={sf("sensitiveData").attachment_required}
                referenceEvidence={
                  (typeof window !== "undefined" &&
                    window.__piaReferenceEvidence?.sensitiveData) ||
                  null
                }
              />
            )}
          </div>
        )}
      </FieldGroup>

      <FieldGroup
        title="Data Subjects & Volume"
        subtitle="Who the data belongs to and processing scale"
        icon={Users}
        color="#0891b2"
      >
        <G2>
          {sf("dataSubjects") && sf("dataSubjects").field_type !== "text" ? (
            <DynamicField
              field={sf("dataSubjects")}
              d={d}
              set={set}
              errors={errors}
            />
          ) : (
            <FTagInput
              label={sf("dataSubjects")?.text || "Data Subjects"}
              id="dataSubjects"
              required={sf("dataSubjects") ? sf("dataSubjects").required : true}
              placeholder='Type a category (e.g. "Customers") then Enter'
              value={d.dataSubjects || []}
              onChange={set("dataSubjects")}
              error={errors.dataSubjects}
            />
          )}
          <FSelect
            label="Volume & Frequency"
            id="volume"
            optional
            icon={Database}
            options={[
              "Low Volume",
              "Medium Volume",
              "High Volume",
              "Very High Volume / Real-time",
            ]}
            value={d.volume || ""}
            onChange={set("volume")}
          />
        </G2>
        {errors.dataSubjects && <ErrBanner errors={[errors.dataSubjects]} />}
      </FieldGroup>

      <SchemaExtraFields
        schemaFields={schemaFields}
        sectionId="dataDetails"
        d={d}
        set={set}
        errors={errors}
      />
    </div>
  );
}

function DataFlowPanel({ d, set, errors, accent, schemaFields = [] }) {
  const sf = (id) => schemaField(schemaFields, id, "dataFlow");

  const storageOpts = schemaOpts(sf("storageLocations")).length
    ? schemaOpts(sf("storageLocations"))
    : STORAGE_OPTIONS;
  const collectionOpts = schemaOpts(sf("collectionMethod")).length
    ? schemaOpts(sf("collectionMethod"))
    : COLLECTION_METHODS;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <FieldGroup
        title="Storage & Collection"
        subtitle="Where data is stored and how it enters your systems"
        icon={Database}
        color={accent}
      >
        {sf("storageLocations") &&
        sf("storageLocations").field_type !== "multiselect" ? (
          <DynamicField
            field={sf("storageLocations")}
            d={d}
            set={set}
            errors={errors}
          />
        ) : (
          <FMultiSelect
            label={sf("storageLocations")?.text || "Storage Locations"}
            id="storageLocations"
            required={
              sf("storageLocations") ? sf("storageLocations").required : true
            }
            options={storageOpts}
            value={d.storageLocations || []}
            onChange={set("storageLocations")}
            error={errors.storageLocations}
          />
        )}
        {errors.storageLocations && (
          <ErrBanner errors={[errors.storageLocations]} />
        )}
        {sf("collectionMethod") &&
        sf("collectionMethod").field_type !== "select" ? (
          <DynamicField
            field={sf("collectionMethod")}
            d={d}
            set={set}
            errors={errors}
          />
        ) : (
          <FSelect
            label={sf("collectionMethod")?.text || "Data Collection Method"}
            id="collectionMethod"
            required={
              sf("collectionMethod") ? sf("collectionMethod").required : true
            }
            icon={Upload}
            options={collectionOpts}
            value={d.collectionMethod || ""}
            onChange={set("collectionMethod")}
            error={errors.collectionMethod}
          />
        )}
        {errors.collectionMethod && (
          <ErrBanner errors={[errors.collectionMethod]} />
        )}
      </FieldGroup>

      <FieldGroup
        title="Transfer Pathways"
        subtitle="All internal and external data movement routes"
        icon={Layers}
        color="#4338ca"
      >
        <G2>
          {sf("internalTransfers") &&
          sf("internalTransfers").field_type !== "textarea" ? (
            <DynamicField
              field={sf("internalTransfers")}
              d={d}
              set={set}
              errors={errors}
            />
          ) : (
            <FTextarea
              label={sf("internalTransfers")?.text || "Internal Transfers"}
              id="internalTransfers"
              required={
                sf("internalTransfers")
                  ? sf("internalTransfers").required
                  : true
              }
              rows={4}
              placeholder="Describe data sharing between internal departments."
              value={d.internalTransfers || ""}
              onChange={set("internalTransfers")}
              error={errors.internalTransfers}
            />
          )}
          {sf("externalTransfers") &&
          sf("externalTransfers").field_type !== "textarea" ? (
            <DynamicField
              field={sf("externalTransfers")}
              d={d}
              set={set}
              errors={errors}
            />
          ) : (
            <FTextarea
              label={sf("externalTransfers")?.text || "External Transfers"}
              id="externalTransfers"
              required={
                sf("externalTransfers")
                  ? sf("externalTransfers").required
                  : true
              }
              rows={4}
              placeholder="Describe all sharing with third-party vendors."
              value={d.externalTransfers || ""}
              onChange={set("externalTransfers")}
              error={errors.externalTransfers}
            />
          )}
        </G2>
        {(errors.internalTransfers || errors.externalTransfers) && (
          <ErrBanner
            errors={[errors.internalTransfers, errors.externalTransfers].filter(
              Boolean,
            )}
          />
        )}
      </FieldGroup>

      <SchemaExtraFields
        schemaFields={schemaFields}
        sectionId="dataFlow"
        d={d}
        set={set}
        errors={errors}
      />
    </div>
  );
}

function RiskPanel({ d, set, errors, accent, schemaFields = [] }) {
  const sf = (id) => schemaField(schemaFields, id, "risk");
  const likelihood = d.likelihood || 1;
  const impact = d.impact || 1;
  const score = likelihood * impact;
  // ... (keep existing rMeta calculation unchanged) ...

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Keep the live risk score card unchanged — it derives from d.likelihood/d.impact */}
      {/* ... existing risk score card JSX ... */}

      <FieldGroup
        title="Risk Scoring"
        subtitle="Rate likelihood and impact on a 1–5 scale"
        icon={AlertTriangle}
        color={accent}
      >
        <G2>
          {/* likelihood — keep slider UI; only switch if type changed */}
          {sf("likelihood") &&
          !["", "rating", "range", undefined].includes(
            sf("likelihood")?.field_type,
          ) ? (
            <DynamicField
              field={sf("likelihood")}
              d={d}
              set={set}
              errors={errors}
            />
          ) : (
            <FRange
              label={sf("likelihood")?.text || "Likelihood of Risk Occurrence"}
              id="likelihood"
              min={1}
              max={5}
              required
              value={likelihood}
              onChange={set("likelihood")}
              color="#ef4444"
            />
          )}
          {sf("impact") &&
          !["", "rating", "range", undefined].includes(
            sf("impact")?.field_type,
          ) ? (
            <DynamicField
              field={sf("impact")}
              d={d}
              set={set}
              errors={errors}
            />
          ) : (
            <FRange
              label={sf("impact")?.text || "Potential Impact Severity"}
              id="impact"
              min={1}
              max={5}
              required
              value={impact}
              onChange={set("impact")}
              color="#f59e0b"
            />
          )}
        </G2>
      </FieldGroup>

      <FieldGroup
        title="Mitigation Strategy"
        subtitle="Document measures to address identified risks"
        icon={Shield}
        color="#4338ca"
      >
        {sf("mitigation") && sf("mitigation").field_type !== "textarea" ? (
          <DynamicField
            field={sf("mitigation")}
            d={d}
            set={set}
            errors={errors}
          />
        ) : (
          <FTextarea
            label={sf("mitigation")?.text || "Mitigation Measures"}
            id="mitigation"
            required={sf("mitigation") ? sf("mitigation").required : true}
            rows={5}
            placeholder="Describe the technical, administrative, and operational controls."
            value={d.mitigation || ""}
            onChange={set("mitigation")}
            error={errors.mitigation}
          />
        )}
        {errors.mitigation && <ErrBanner errors={[errors.mitigation]} />}
      </FieldGroup>

      <SchemaExtraFields
        schemaFields={schemaFields}
        sectionId="risk"
        d={d}
        set={set}
        errors={errors}
      />
    </div>
  );
}

function VendorPanel({ d, set, errors, accent, schemaFields = [] }) {
  const sf = (id) => schemaField(schemaFields, id, "vendorControls");

  const controlTypesSF = sf("controlTypes");
  const controlTypeOpts = schemaOpts(controlTypesSF).length
    ? schemaOpts(controlTypesSF)
    : CONTROL_TYPES;

  const complianceStatusSF = sf("complianceStatus");
  const complianceStatusOpts = schemaOpts(complianceStatusSF).length
    ? schemaOpts(complianceStatusSF)
    : COMPLIANCE_STATUSES;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <FieldGroup
        title="Compliance & Control Framework"
        subtitle="Control types and compliance certification status"
        icon={BookOpen}
        color={accent}
      >
        <G2>
          <FSelect
            label={controlTypesSF?.text || "Control Type"}
            id="controlTypes"
            required={controlTypesSF ? controlTypesSF.required : true}
            icon={ShieldCheck}
            options={controlTypeOpts}
            value={d.controlTypes || ""}
            onChange={set("controlTypes")}
            error={errors.controlTypes}
          />
          <FSelect
            label={complianceStatusSF?.text || "Compliance Status"}
            id="complianceStatus"
            required={complianceStatusSF ? complianceStatusSF.required : true}
            icon={CheckCircle2}
            options={complianceStatusOpts}
            value={d.complianceStatus || ""}
            onChange={set("complianceStatus")}
            error={errors.complianceStatus}
          />
        </G2>
        {(errors.controlTypes || errors.complianceStatus) && (
          <ErrBanner
            errors={[errors.controlTypes, errors.complianceStatus].filter(
              Boolean,
            )}
          />
        )}
        <FTextarea
          label="Certifications Held"
          id="certifications"
          optional
          rows={3}
          placeholder="e.g., ISO 27001, SOC 2 Type II, GDPR DPA, HIPAA BAA, PCI-DSS"
          value={d.certifications || ""}
          onChange={set("certifications")}
        />
      </FieldGroup>

      <FieldGroup
        title="Vendor Details & Geographic Footprint"
        subtitle="Third-party processor information and data residency"
        icon={Globe}
        color="#0891b2"
      >
        <FTextarea
          label="Sub-processors"
          id="subProcessors"
          optional
          rows={3}
          placeholder="List all sub-processors with their names, roles, and the specific processing they perform."
          value={d.subProcessors || ""}
          onChange={set("subProcessors")}
        />
        <FTextarea
          label="Data Locations"
          id="dataLocations"
          required
          rows={3}
          placeholder="List all geographic locations — countries and regions — where data is stored, processed, backed up, or accessible."
          value={d.dataLocations || ""}
          onChange={set("dataLocations")}
          error={errors.dataLocations}
        />
        {errors.dataLocations && <ErrBanner errors={[errors.dataLocations]} />}
        <G2>
          <FInput
            label="Vendor DPO / Contact"
            id="vendorContact"
            optional
            icon={Users}
            placeholder="Name or email of vendor data protection officer"
            value={d.vendorContact || ""}
            onChange={set("vendorContact")}
          />
          <FInput
            label="Contract Expiry Date"
            id="contractExpiry"
            optional
            icon={Clock}
            type="date"
            value={d.contractExpiry || ""}
            onChange={set("contractExpiry")}
          />
        </G2>
      </FieldGroup>
      <SchemaExtraFields
        schemaFields={schemaFields}
        sectionId="vendorControls"
        d={d}
        set={set}
        errors={errors}
      />
    </div>
  );
}

function SafeguardsPanel({ d, set, errors, accent, schemaFields = [] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <FieldGroup
        title="Regulatory Safeguards"
        subtitle="Specific protections for each applicable regulation"
        icon={Shield}
        color={accent}
      >
        <FTextarea
          label="GDPR Safeguards"
          id="gdprSafeguards"
          required
          rows={4}
          placeholder="e.g., End-to-end encryption, DPIA completed, RBAC access controls, Data minimisation applied, Right to erasure procedures documented, DPA with all processors signed."
          value={d.gdprSafeguards || ""}
          onChange={set("gdprSafeguards")}
          error={errors.gdprSafeguards}
        />
        {errors.gdprSafeguards && (
          <ErrBanner errors={[errors.gdprSafeguards]} />
        )}
        <FTextarea
          label="DPDP Act 2023 Safeguards"
          id="dpdpSafeguards"
          required
          rows={4}
          placeholder="Specific safeguards under India's Digital Personal Data Protection Act 2023 — consent management, grievance mechanisms, and data fiduciary obligations."
          value={d.dpdpSafeguards || ""}
          onChange={set("dpdpSafeguards")}
          error={errors.dpdpSafeguards}
        />
        {errors.dpdpSafeguards && (
          <ErrBanner errors={[errors.dpdpSafeguards]} />
        )}
      </FieldGroup>

      <FieldGroup
        title="Transfer Justification"
        subtitle="Legal and operational justification for all data transfers"
        icon={FileText}
        color="#7c3aed"
      >
        <FTextarea
          label="Transfer Justification"
          id="transferJustification"
          required
          rows={4}
          placeholder="Provide the legal basis for each data transfer — including lawfulness under GDPR Art. 44-49, adequacy decisions relied upon, and any SCCs or BCRs in place."
          value={d.transferJustification || ""}
          onChange={set("transferJustification")}
          error={errors.transferJustification}
        />
        {errors.transferJustification && (
          <ErrBanner errors={[errors.transferJustification]} />
        )}
      </FieldGroup>

      <FieldGroup
        title="Final Review"
        subtitle="Reviewer details and compliance declaration"
        icon={CheckCircle2}
        color="#059669"
      >
        <G2>
          <FInput
            label="Reviewer Full Name"
            id="reviewerName"
            optional
            icon={Users}
            placeholder="Full name of the PIA reviewer"
            value={d.reviewerName || ""}
            onChange={set("reviewerName")}
          />
          <FInput
            label="Review Date"
            id="reviewDate"
            optional
            icon={Clock}
            type="date"
            value={d.reviewDate || ""}
            onChange={set("reviewDate")}
          />
        </G2>
        <button
          onClick={() => set("acknowledged")(!d.acknowledged)}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 14,
            padding: "16px",
            borderRadius: 12,
            width: "100%",
            border: `1.5px solid ${d.acknowledged ? "#6366f1" : "rgba(99,102,241,0.18)"}`,
            background: d.acknowledged
              ? "linear-gradient(135deg,#eef2ff,#e0e7ff)"
              : "#f8fafc",
            cursor: "pointer",
            textAlign: "left",
            transition: "all 0.2s",
            fontFamily: "inherit",
            boxShadow: d.acknowledged
              ? "0 0 0 3px rgba(99,102,241,0.1)"
              : "none",
          }}
        >
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: 6,
              border: `2px solid ${d.acknowledged ? "#6366f1" : "#d1d5db"}`,
              flexShrink: 0,
              marginTop: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: d.acknowledged
                ? "linear-gradient(135deg,#6366f1,#8b5cf6)"
                : "transparent",
              boxShadow: d.acknowledged
                ? "0 2px 6px rgba(99,102,241,0.4)"
                : "none",
              transition: "all 0.15s",
            }}
          >
            {d.acknowledged && <Check size={11} color="#fff" />}
          </div>
          <div>
            <p
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: d.acknowledged ? "#4338ca" : "#1e293b",
                marginBottom: 4,
              }}
            >
              I acknowledge and declare accuracy
            </p>
            <p style={{ fontSize: 12, color: "#334155", lineHeight: 1.6 }}>
              This Privacy Impact Assessment has been completed accurately to
              the best of my knowledge and complies with all applicable data
              protection regulations including{" "}
              <strong style={{ color: "#1e293b" }}>GDPR</strong> and the{" "}
              <strong style={{ color: "#1e293b" }}>DPDP Act 2023</strong>.
            </p>
          </div>
        </button>
      </FieldGroup>
      <SchemaExtraFields
        schemaFields={schemaFields}
        sectionId="safeguards"
        d={d}
        set={set}
        errors={errors}
      />
    </div>
  );
}

function ErrBanner({ errors }) {
  if (!errors.length) return null;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "10px 14px",
        borderRadius: 10,
        background: "#fff1f2",
        border: "1px solid #fca5a5",
      }}
    >
      {errors.map((e, i) => (
        <p
          key={i}
          style={{
            fontSize: 11.5,
            color: "#ef4444",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              background: "#ef4444",
              flexShrink: 0,
              display: "inline-block",
            }}
          />
          {e}
        </p>
      ))}
    </div>
  );
}

// ─── Sidebar inner ────────────────────────────────────────────────────────────
function SidebarContent({ step, completed, errors, onGoTo, data }) {
  const pct = Math.round((completed.length / STEPS.length) * 100);
  const fillColor = pct >= 75 ? "#10b981" : pct >= 40 ? "#6366f1" : "#475569";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "12px 12px 16px",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(99,102,241,0.4) #e2e8f0",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "10px 12px",
            marginBottom: 8,
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#1e293b",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              PIA Progress
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 20,
                background: fillColor + "15",
                color: fillColor,
                border: `1px solid ${fillColor}30`,
              }}
            >
              {pct >= 75 ? "On Track" : pct >= 40 ? "In Progress" : "Started"}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <ArcGauge value={pct} size={52} stroke={6} color="#10b981" />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: "#0f172a",
                    lineHeight: 1,
                  }}
                >
                  {pct}%
                </span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {[
                { l: "Complete", v: completed.length, c: "#10b981" },
                {
                  l: "Pending",
                  v: STEPS.length - completed.length,
                  c: "#475569",
                },
                { l: "Total", v: STEPS.length, c: "#6366f1" },
              ].map((x) => (
                <div
                  key={x.l}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 4,
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: x.c,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 10, color: "#334155", flex: 1 }}>
                    {x.l}
                  </span>
                  <span
                    style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}
                  >
                    {x.v}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              marginTop: 8,
              height: 3,
              borderRadius: 9,
              background: "#f1f5f9",
              overflow: "hidden",
              display: "flex",
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                background: `linear-gradient(90deg,#6366f1,${fillColor})`,
                height: "100%",
                transition: "width 0.7s ease",
                borderRadius: 9,
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 5,
              fontSize: 10,
              color: "#334155",
            }}
          >
            <span>{completed.length} done</span>
            <span>{STEPS.length} steps</span>
          </div>
        </div>
        {data && (
          <FormScorePanel
            scorePath="/pia/score"
            formData={data}
            levelLabel="Risk Level"
            accent="#6366f1"
            variant="sidebar"
          />
        )}
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "#0f172a",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            padding: "2px 4px 8px",
          }}
        >
          Assessment Steps
        </div>

        {STEPS.map((s, i) => {
          const done = completed.includes(i);
          const active = i === step;
          const hasErr = i === step && Object.keys(errors).length > 0;
          return (
            <button
              key={s.id}
              onClick={() => onGoTo(i)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 10px",
                borderRadius: 12,
                border: `1px solid ${active ? s.accent + "35" : hasErr ? "#fca5a580" : "transparent"}`,
                background: active
                  ? `linear-gradient(135deg,${s.accent}18 0%,${s.accent}0a 100%)`
                  : "transparent",
                cursor: "pointer",
                transition: "all 0.15s ease",
                textAlign: "left",
                marginBottom: 2,
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: active
                    ? `linear-gradient(135deg,${s.accent}cc,${s.accent}66)`
                    : done
                      ? "#ecfdf5"
                      : "linear-gradient(135deg,#f1f5f9,#e8eaf6)",
                  border: `1px solid ${active ? s.accent + "50" : done ? "#a7f3d0" : "rgba(99,102,241,0.1)"}`,
                  boxShadow: active ? `0 2px 8px ${s.accent}35` : "none",
                }}
              >
                {done && !active ? (
                  <Check size={12} color="#059669" />
                ) : (
                  <s.Icon size={13} color={active ? "#fff" : "#0f172a"} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#0f172a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {s.title}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: active ? s.accent : "#1e293b",
                    marginTop: 1,
                    fontWeight: 500,
                  }}
                >
                  {s.code} ·{" "}
                  {done ? "Complete" : active ? "In progress" : "Pending"}
                </div>
              </div>
              {done && !active && (
                <CheckCircle2
                  size={12}
                  color="#10b981"
                  style={{ flexShrink: 0 }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Result screen ────────────────────────────────────────────────────────────
function ResultScreen({ data, completed, onReset }) {
  const [exporting, setExporting] = useState(false);
  const pct = Math.round((completed.length / STEPS.length) * 100);
  const sc = pct >= 75 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444";
  const sl =
    pct >= 75
      ? "PIA Submitted Successfully"
      : pct >= 50
        ? "Partially Complete"
        : "Incomplete Submission";

  const handleExport = async () => {
    setExporting(true);
    try {
      const riskScore = data.risk
        ? Number(data.risk.likelihood || 1) * Number(data.risk.impact || 1) * 4
        : 0;
      const riskLevel =
        riskScore >= 70
          ? "Critical"
          : riskScore >= 50
            ? "High"
            : riskScore >= 30
              ? "Medium"
              : "Low";

      const payload = PIA_CONFIG.buildExportSections(
        {
          form_data: data,
          risk_score: riskScore,
          risk_level: riskLevel,
          display_name: data.general?.projectName || "",
          vendor: data.general?.vendor || "",
          created_at: new Date().toISOString(),
        },
        { forExcel: false },
      );

      await exportSubmissionPdf({
        title: data.general?.projectName || "PIA Submission",
        subtitle: `PIA · ${new Date().toLocaleDateString()}`,
        module: "PIA",
        organization: data.general?.vendor || "",
        sections: payload,
      });
    } catch (err) {
      showError(err?.message || "Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      style={{
        flex: 1,
        height: "100%",
        overflowY: "auto",
        background: "#ffffff",
        fontFamily: "'DM Sans',system-ui,sans-serif",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(99,102,241,0.3) #e2e8f0",
      }}
    >
      <div
        style={{
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <div style={{ maxWidth: 540, width: "100%" }}>
          <div
            style={{
              borderRadius: 24,
              padding: "32px 24px",
              textAlign: "center",
              background: "#ffffff",
              border: "1px solid rgba(99,102,241,0.15)",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 18,
                background:
                  "linear-gradient(135deg,#6366f1 0%,#8b5cf6 50%,#a855f7 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                boxShadow:
                  "0 8px 32px rgba(99,102,241,0.4),0 4px 12px rgba(139,92,246,0.3)",
              }}
            >
              <Award size={26} color="#fff" />
            </div>
            <h2
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#0f172a",
                marginBottom: 4,
              }}
            >
              PIA Submitted
            </h2>
            <p style={{ fontSize: 12, color: "#334155", marginBottom: 24 }}>
              Privacy Impact Assessment · GDPR & DPDP Act 2023
            </p>
            <div
              style={{
                position: "relative",
                display: "inline-block",
                marginBottom: 12,
              }}
            >
              <ArcGauge value={pct} size={120} stroke={11} color={sc} />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 28,
                    fontWeight: 800,
                    color: "#0f172a",
                    lineHeight: 1,
                  }}
                >
                  {pct}%
                </span>
                <span
                  style={{ fontSize: 10, fontWeight: 600, color: "#1e293b" }}
                >
                  complete
                </span>
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: sc }}>{sl}</div>
          </div>

          <div
            style={{
              borderRadius: 20,
              padding: 20,
              background: "#ffffff",
              border: "1px solid rgba(99,102,241,0.12)",
              marginBottom: 16,
            }}
          >
            <h3
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#334155",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 14,
              }}
            >
              Step Completion
            </h3>
            {STEPS.map((s, i) => {
              const done = completed.includes(i);
              return (
                <div
                  key={s.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 10,
                  }}
                >
                  <s.Icon
                    size={11}
                    color={s.accent}
                    style={{ flexShrink: 0 }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      color: "#0f172a",
                      fontWeight: 600,
                      width: 160,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {s.title}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: 5,
                      borderRadius: 9,
                      background: "#f1f5f9",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: done ? "100%" : "0%",
                        height: "100%",
                        background: done ? s.accent : "#e2e8f0",
                        borderRadius: 9,
                        transition: "width 1s cubic-bezier(0.4,0,0.2,1)",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      color: done ? s.accent : "#334155",
                      fontWeight: 700,
                      width: 40,
                      textAlign: "right",
                      flexShrink: 0,
                    }}
                  >
                    {done ? "Done" : "–"}
                  </span>
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={onReset}
              disabled={exporting}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: 14,
                borderRadius: 14,
                background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                border: "none",
                cursor: "pointer",
                boxShadow: "0 6px 24px rgba(99,102,241,0.38)",
                fontFamily: "inherit",
                opacity: exporting ? 0.6 : 1,
              }}
            >
              <RefreshCw size={14} /> Submit Another PIA
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: 14,
                borderRadius: 14,
                background: "#ffffff",
                color: "#1e293b",
                fontSize: 13,
                fontWeight: 600,
                border: "1px solid rgba(99,102,241,0.2)",
                cursor: exporting ? "wait" : "pointer",
                fontFamily: "inherit",
                opacity: exporting ? 0.6 : 1,
              }}
            >
              <Download size={14} />{" "}
              {exporting ? "Exporting…" : "Export Record"}
            </button>
          </div>
          <p
            style={{
              textAlign: "center",
              fontSize: 10,
              color: "#64748b",
              marginTop: 16,
            }}
          >
            Ref #PIA-{Date.now().toString(36).toUpperCase()} · PIA
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── State initialiser ────────────────────────────────────────────────────────
function initData() {
  return {
    general: {
      vendor: "",
      projectName: "",
      ownerDept: "",
      procDesc: "",
      bizPurpose: "",
      legalBasis: [],
      duration: "",
    },
    dataDetails: {
      dataTypes: [],
      sensitiveData: "",
      dataSubjects: [],
      volume: "",
      retention: "",
      appOrigin: "",
      appDest: "",
    },
    dataFlow: {
      storageLocations: [],
      otherStorage: "",
      collectionMethod: "",
      internalTransfers: "",
      externalTransfers: "",
      crossBorder: "",
      processors: "",
    },
    risk: { likelihood: 1, impact: 1, riskCategory: "", mitigation: "" },
    vendorControls: {
      controlTypes: "",
      complianceStatus: "",
      subProcessors: "",
      dataLocations: "",
      certifications: "",
      vendorContact: "",
      contractExpiry: "",
    },
    safeguards: {
      gdprSafeguards: "",
      dpdpSafeguards: "",
      transferJustification: "",
      reviewerName: "",
      reviewDate: "",
      acknowledged: false,
    },
  };
}

function detectLocked(draft) {
  if (!draft) return false;
  return Boolean(
    draft.is_locked ??
    draft.is_submitted ??
    draft.submitted ??
    (draft.status && String(draft.status).toLowerCase() === "submitted"),
  );
}

function mergePiaData(draft) {
  const base = initData();
  if (!draft || typeof draft !== "object") return base;
  const merged = { ...base };
  Object.keys(base).forEach((stepId) => {
    merged[stepId] = { ...base[stepId], ...(draft[stepId] || {}) };
  });
  const lb = merged.general?.legalBasis;
  if (typeof lb === "string") {
    merged.general.legalBasis = lb ? [lb] : [];
  } else if (!Array.isArray(lb)) {
    merged.general.legalBasis = [];
  }
  return merged;
}

// ─── Section completion ─────────────────────────────────────────────────────
// A section is "complete" exactly when it has no outstanding validation
// errors — i.e. the same rule used to gate "Next"/"Submit". This keeps the
// sidebar tick in sync with what actually blocks submission, instead of
// requiring every field (including ones the UI marks "Optional").
function isStepComplete(stepIdx, data, schemaFields) {
  return Object.keys(validate(stepIdx, data, schemaFields)).length === 0;
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function PIAForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState([]);
  const [data, setData] = useState(() => initData());
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [loadingDept, setLoadingDept] = useState(false);
  const [schemaFields, setSchemaFields] = useState([]); // ← add
  const [referenceEvidence, setReferenceEvidence] = useState({});
  const [isLocked, setIsLocked] = useState(false);
  const [lockInfo, setLockInfo] = useState(null);
  const { departments, loading: departmentsLoading } = useDepartments();
  useAutoSelectAssignedDepartment({
    departmentId,
    setDepartmentId,
    departments,
    loading: departmentsLoading,
  });
  const { currentCycleId, currentCycle } = useAssessmentCycles("pia");
  const isCycleClosed =
    String(currentCycle?.status || "open").toLowerCase() === "closed";
  const formReadOnly = isLocked || isCycleClosed;
  const liveTour = useLiveTourOptional();
  const tourPreviewDepartmentId = getLiveTourPreviewDepartmentId(
    liveTour,
    departments,
    departmentsLoading,
  );
  const activeDepartmentId = departmentId || tourPreviewDepartmentId;
  useLiveTourDepartmentPrep(
    departmentId,
    setDepartmentId,
    departments,
    departmentsLoading,
  );
  const saveTimer = useRef(null);
  const mainRef = useRef();

  // Fetch tenant schema on mount — overrides hardcoded fields if tenant customised
  const loadPiaReferenceEvidence = useCallback((fields) => {
    const ids = [
      ...new Set(
        (fields || [])
          .flatMap((f) => [f.external_id, f.id])
          .filter(Boolean),
      ),
    ];
    if (!ids.length) return;
    apiFetch(
      `/pia/reference-evidence?question_ids=${encodeURIComponent(ids.join(","))}`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((refData) => {
        const items = refData?.items || {};
        setReferenceEvidence(items);
        window.__piaReferenceEvidence = items;
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadSchema = () => {
      apiFetch("/pia/schema")
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.fields?.length) {
            const normalized = normalizePiaSchemaFields(data.fields);
            setSchemaFields(normalized);
            window.__piaSchemaFields = normalized;
            loadPiaReferenceEvidence(normalized);
          }
        })
        .catch(() => {});
    };
    loadSchema();
    const onFocus = () => loadSchema();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadPiaReferenceEvidence]);

  useEffect(() => {
    const hasPending = Object.values(referenceEvidence).some((item) =>
      ["pending", "generating"].includes(item?.generation_status),
    );
    if (!hasPending || !schemaFields.length) return undefined;
    const timer = setInterval(
      () => loadPiaReferenceEvidence(schemaFields),
      8000,
    );
    return () => clearInterval(timer);
  }, [referenceEvidence, schemaFields, loadPiaReferenceEvidence]);

  const buildPayload = useCallback(
    () => ({
      step,
      form_data: data,
      department_id: departmentId || null,
      cycle_id: currentCycleId || null,
    }),
    [step, data, departmentId, currentCycleId],
  );

  const handleDepartmentChange = (e) => {
    clearTimeout(saveTimer.current);
    setDraftLoaded(false);
    setDepartmentId(e.target.value);
  };

  const loadDepartmentAssessment = useCallback(async (deptId) => {
    if (!deptId) {
      setData(initData());
      setStep(0);
      setErrors({});
      setIsLocked(false);
      setLockInfo(null);
      setDraftLoaded(true);
      return;
    }
    setLoadingDept(true);
    setDraftLoaded(false);
    try {
      const q = encodeURIComponent(deptId);
      const cycleQ = currentCycleId
        ? `&cycle_id=${encodeURIComponent(currentCycleId)}`
        : "";
      const res = await apiFetch(`/pia/draft?department_id=${q}${cycleQ}`);
      if (res.ok) {
        const draft = await res.json();
        const locked = detectLocked(draft);
        setIsLocked(locked);
        setLockInfo(
          locked
            ? {
                submittedAt: draft.submitted_at || draft.updated_at || null,
                submittedBy:
                  draft.submitted_by_name || draft.submitted_by || null,
              }
            : null,
        );
        if (draft.form_data && Object.keys(draft.form_data).length) {
          setData(mergePiaData(draft.form_data));
          if (typeof draft.step === "number") setStep(draft.step);
        } else {
          setData(initData());
          setStep(0);
        }
      } else {
        setData(initData());
        setStep(0);
        setIsLocked(false);
        setLockInfo(null);
      }
      setErrors({});
      setSubmitError("");
    } catch {
      setData(initData());
      setStep(0);
      setIsLocked(false);
      setLockInfo(null);
    } finally {
      setLoadingDept(false);
      setDraftLoaded(true);
    }
  }, [currentCycleId]);

  useEffect(() => {
    loadDepartmentAssessment(activeDepartmentId);
  }, [activeDepartmentId, currentCycleId, loadDepartmentAssessment]);

  useEffect(() => {
    if (!draftLoaded || submitted || isLocked || isCycleClosed || !departmentId)
      return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await apiFetch("/pia/draft", {
          method: "PUT",
          body: JSON.stringify(buildPayload()),
        });
      } catch {
        // silent
      } finally {
        setSaving(false);
      }
    }, 900);
    return () => clearTimeout(saveTimer.current);
  }, [
    draftLoaded,
    submitted,
    isLocked,
    isCycleClosed,
    departmentId,
    buildPayload,
  ]);

  useEffect(() => {
    setCompleted(
      STEPS.map((_, i) => i).filter((i) =>
        isStepComplete(i, data, schemaFields),
      ),
    );
  }, [data, schemaFields]);

  const set = useCallback(
    (key) => (val) =>
      setData((prev) => ({
        ...prev,
        [STEPS[step].id]: { ...prev[STEPS[step].id], [key]: val },
      })),
    [step],
  );

  const makeSet = useCallback(
    (id) => (key) => (val) =>
      setData((prev) => ({ ...prev, [id]: { ...prev[id], [key]: val } })),
    [],
  );

  const goTo = (i) => {
    if (i === step || transitioning) return;
    setTransitioning(true);
    setTimeout(() => {
      setStep(i);
      setErrors({});
      setTransitioning(false);
    }, 200);
    setMobileNav(false);
    if (mainRef.current)
      mainRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const onTourLastSection = () => {
      if (STEPS.length === 0) return;
      const last = STEPS.length - 1;
      setStep(last);
      setErrors({});
      setMobileNav(false);
      window.setTimeout(() => {
        mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        notifyLiveTourTargetsChanged();
      }, 280);
    };
    window.addEventListener("live-tour:go-to-last-section", onTourLastSection);
    return () =>
      window.removeEventListener("live-tour:go-to-last-section", onTourLastSection);
  }, []);

  const focusValidationErrors = useCallback((errs) => {
    afterValidationRender(() => {
      scrollToFirstInvalidField(Object.keys(errs), { container: mainRef });
    });
  }, []);

  const handleSubmit = async () => {
    if (formReadOnly) {
      setSubmitError(
        isLocked
          ? "This PIA has already been submitted for this department and is locked for editing."
          : "This assessment cycle is closed. Contact your tenant admin to reopen it.",
      );
      return;
    }
    if (!departmentId) {
      setSubmitError("Please select your department before submitting.");
      mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const errs = validate(step, data, schemaFields);
    if (Object.keys(errs).length) {
      setErrors(errs);
      focusValidationErrors(errs);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await apiFetch("/pia/submit", {
        method: "POST",
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }
      setSubmitted(true);
    } catch (e) {
      setSubmitError(e.message || "Could not submit PIA");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (formReadOnly) {
      if (step < STEPS.length - 1) goTo(step + 1);
      return;
    }
    const errs = validate(step, data, schemaFields);
    if (Object.keys(errs).length) {
      setErrors(errs);
      focusValidationErrors(errs);
      return;
    }
    setErrors({});
    if (step === STEPS.length - 1) {
      handleSubmit();
      return;
    }
    goTo(step + 1);
  };

  const sec = STEPS[step];
  const sData = data[sec.id] || {};
  const secSet = makeSet(sec.id);

  if (submitted) {
    return (
      <>
        <style>{CSS}</style>
        <ResultScreen
          data={data}
          completed={completed}
          onReset={() => {
            setSubmitted(false);
            setStep(0);
            setCompleted([]);
            setData(initData());
            setErrors({});
          }}
        />
      </>
    );
  }

  // Build a lookup: field external_id → field def, filtered to current section
  const sectionFields = (sectionId) =>
    schemaFields.filter((f) => f.section_id === sectionId);

  const panels = {
    general: (
      <GeneralPanel
        d={sData}
        set={secSet}
        errors={errors}
        accent={sec.accent}
        schemaFields={sectionFields("general")}
      />
    ),
    dataDetails: (
      <DataDetailsPanel
        d={sData}
        set={secSet}
        errors={errors}
        accent={sec.accent}
        schemaFields={sectionFields("dataDetails")}
      />
    ),
    dataFlow: (
      <DataFlowPanel
        d={sData}
        set={secSet}
        errors={errors}
        accent={sec.accent}
        schemaFields={sectionFields("dataFlow")}
      />
    ),
    risk: (
      <RiskPanel
        d={sData}
        set={secSet}
        errors={errors}
        accent={sec.accent}
        schemaFields={sectionFields("risk")}
      />
    ),
    vendorControls: (
      <VendorPanel
        d={sData}
        set={secSet}
        errors={errors}
        accent={sec.accent}
        schemaFields={sectionFields("vendorControls")}
      />
    ),
    safeguards: (
      <SafeguardsPanel
        d={sData}
        set={secSet}
        errors={errors}
        accent={sec.accent}
        schemaFields={sectionFields("safeguards")}
      />
    ),
  };
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        background: "#ffffff",
        fontFamily: "'DM Sans',system-ui,-apple-system,sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{CSS}</style>

      {/* Header */}
      <header
        data-tour-id="tour-pia-header"
        style={{
          background: "#ffffff",
          boxShadow:
            "0 1px 0 0 rgba(99,102,241,0.18),0 2px 8px rgba(0,0,0,0.04)",
          borderBottom: "1px solid rgba(99,102,241,0.10)",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => setMobileNav(true)}
          className="pia-mob-btn"
          style={{
            padding: 6,
            borderRadius: 8,
            background: "#ffffff",
            border: "1px solid rgba(99,102,241,0.15)",
            color: "#334155",
            flexShrink: 0,
            cursor: "pointer",
            display: "none",
          }}
        >
          <Menu size={18} />
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background:
                "linear-gradient(135deg,#6366f1 0%,#8b5cf6 50%,#a855f7 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 4px 18px rgba(99,102,241,0.55),0 2px 8px rgba(139,92,246,0.35)",
            }}
          >
            <ShieldCheck size={16} color="#fff" />
          </div>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              PIA
            </div>
            <div style={{ fontSize: 10, color: "#334155", lineHeight: 1.2 }}>
              Privacy Impact Assessment
            </div>
          </div>
        </div>

        <div
          data-tour-id="tour-pia-department"
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          <DepartmentDropdown
            value={departmentId}
            onChange={handleDepartmentChange}
            departments={departments}
            loading={departmentsLoading}
            lockToAssigned
            style={{ flex: "1 1 200px", maxWidth: 320 }}
          />
          {isLocked && departmentId && (
            <span
              title="This department's PIA has already been submitted and is read-only"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                marginLeft: 8,
                padding: "5px 10px",
                borderRadius: 999,
                background: "linear-gradient(135deg,#f8fafc,#eef2f7)",
                border: "1px solid #cbd5e1",
                color: "#475569",
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 0.2,
                flexShrink: 0,
              }}
            >
              <Lock size={11} /> Locked
            </span>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <div
            data-tour-id="tour-pia-progress"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "6px 14px",
              borderRadius: 10,
              background: "#ffffff",
              border: "1px solid rgba(99,102,241,0.15)",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                {completed.length}
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#1e293b" }}>
                Done
              </div>
            </div>
            <div
              style={{
                width: 1,
                height: 22,
                background: "rgba(99,102,241,0.2)",
              }}
            />
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                {step + 1}
                <span style={{ color: "#334155", fontWeight: 500 }}>
                  /{STEPS.length}
                </span>
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#1e293b" }}>
                Step
              </div>
            </div>
          </div>
          {loadingDept && (
            <span style={{ fontSize: 10, color: "#64748b", fontWeight: 500 }}>
              Loading…
            </span>
          )}
          {saving && (
            <span style={{ fontSize: 10, color: "#64748b", fontWeight: 500 }}>
              Saving…
            </span>
          )}
          {submitError && (
            <span style={{ fontSize: 10, color: "#ef4444", fontWeight: 500 }}>
              {submitError}
            </span>
          )}
          <button
            data-tour-id="tour-pia-view-table"
            onClick={() => navigate("/user/pia/submissions")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              borderRadius: 10,
              background: "#ffffff",
              color: "#6366f1",
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid rgba(99,102,241,0.25)",
              boxShadow: "0 2px 8px rgba(99,102,241,0.1)",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <LayoutList size={13} /> View Table
          </button>
          {isLocked ? (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 16px",
                borderRadius: 10,
                background: "#f1f5f9",
                color: "#64748b",
                fontSize: 11,
                fontWeight: 600,
                border: "1px solid #cbd5e1",
                fontFamily: "inherit",
              }}
            >
              <Lock size={13} /> Read-only
            </span>
          ) : (
            <button
              data-tour-id="tour-pia-submit"
              onClick={handleNext}
              disabled={submitting || !departmentId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 16px",
                borderRadius: 10,
                background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
                border: "none",
                cursor: submitting || !departmentId ? "not-allowed" : "pointer",
                opacity: submitting || !departmentId ? 0.7 : 1,
                fontFamily: "inherit",
              }}
            >
              <ShieldCheck size={13} />{" "}
              {submitting
                ? "Submitting…"
                : step === STEPS.length - 1
                  ? "Submit PIA"
                  : "Save & Continue"}
            </button>
          )}
        </div>
      </header>

      {!departmentId && (
        <div
          style={{
            flexShrink: 0,
            padding: "8px 16px",
            background: "#fffbeb",
            borderBottom: "1px solid rgba(245,158,11,0.2)",
            fontSize: 11,
            color: "#92400e",
            textAlign: "center",
          }}
        >
          Select a department above — each department has its own PIA draft and
          submission.
        </div>
      )}

      {isLocked && departmentId && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "8px 16px",
            background: "#f8fafc",
            borderBottom: "1px solid #cbd5e1",
            fontSize: 11.5,
            color: "#475569",
            textAlign: "center",
          }}
        >
          <Lock size={12} />
          This PIA has already been submitted for this department. You&rsquo;re
          viewing the submitted responses in read-only mode.
          {lockInfo?.submittedAt && (
            <span style={{ color: "#94a3b8" }}>
              &nbsp;· Submitted{" "}
              {new Date(lockInfo.submittedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      {!isLocked && isCycleClosed && departmentId && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "8px 16px",
            background: "#f8fafc",
            borderBottom: "1px solid #cbd5e1",
            fontSize: 11.5,
            color: "#475569",
            textAlign: "center",
          }}
        >
          <Lock size={12} />
          {currentCycle?.name
            ? `${currentCycle.name} is`
            : "This assessment cycle is"}{" "}
          closed. The form is read-only until your tenant admin reopens the
          cycle.
        </div>
      )}

      <div
        style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}
      >
        {!activeDepartmentId ? (
          <DepartmentSelectionRequired message="Please select a department from the dropdown in the header to load, view, or fill the PIA form." />
        ) : (
          <>
        {/* Mobile overlay */}
        {mobileNav && (
          <Overlay
            open={mobileNav}
            onClose={() => setMobileNav(false)}
            className="bg-slate-900/25 backdrop-blur-sm"
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 280,
                background: "#ffffff",
                borderRight: "1px solid rgba(99,102,241,0.15)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  borderBottom: "1px solid rgba(99,102,241,0.12)",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}
                >
                  Navigation
                </span>
                <button
                  onClick={() => setMobileNav(false)}
                  style={{
                    color: "#334155",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <X size={16} />
                </button>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                <SidebarContent
                  step={step}
                  completed={completed}
                  errors={errors}
                  onGoTo={goTo}
                  data={data}
                />
              </div>
            </div>
          </Overlay>
        )}

        {/* Desktop sidebar */}
        <aside
          data-tour-id="tour-pia-sidebar"
          className="pia-desktop-sidebar"
          style={{
            width: 260,
            flexShrink: 0,
            background: "#ffffff",
            borderRight: "1px solid rgba(99,102,241,0.14)",
            boxShadow: "2px 0 12px rgba(99,102,241,0.06)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <SidebarContent
            step={step}
            completed={completed}
            errors={errors}
            onGoTo={goTo}
            data={data}
          />
        </aside>

        {/* Main */}
        <main
          ref={mainRef}
          style={{
            flex: 1,
            overflowY: "auto",
            height: "100%",
            background: "#ffffff",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(99,102,241,0.3) #e2e8f0",
            position: "relative",
          }}
        >
            <div
              style={{
                width: "100%",
                padding: "20px 24px 40px",
              }}
            >
              {/* Validation banner */}
              {Object.keys(errors).length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "12px 16px",
                    borderRadius: 12,
                    background: "#fff1f2",
                    border: "1px solid #fca5a5",
                    marginBottom: 16,
                  }}
                >
                  <AlertTriangle
                    size={15}
                    color="#ef4444"
                    style={{ flexShrink: 0, marginTop: 1 }}
                  />
                  <div>
                    <p
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: "#b91c1c",
                        marginBottom: 2,
                      }}
                    >
                      Please fix {Object.keys(errors).length} required{" "}
                      {Object.keys(errors).length === 1 ? "field" : "fields"}{" "}
                      before continuing
                    </p>
                    <p style={{ fontSize: 11.5, color: "#dc2626" }}>
                      Required fields are marked with a red asterisk{" "}
                      <strong>*</strong>
                    </p>
                  </div>
                  <button
                    onClick={() => setErrors({})}
                    style={{
                      marginLeft: "auto",
                      color: "#fca5a5",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      flexShrink: 0,
                      display: "flex",
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Section banner */}
              <div
                data-tour-id="tour-pia-section-banner"
                style={{
                  borderRadius: 20,
                  padding: "18px 20px",
                  marginBottom: 18,
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "flex-start", gap: 14 }}
                >
                  <div
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 14,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      background: `linear-gradient(135deg,${sec.accent}ee 0%,${sec.accent}88 100%)`,
                      border: `1px solid ${sec.accent}40`,
                      boxShadow: `0 4px 16px ${sec.accent}40`,
                    }}
                  >
                    <sec.Icon size={20} color="#fff" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <h2
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        {sec.title}
                      </h2>
                      <span
                        style={{
                          fontSize: 10,
                          fontFamily: "monospace",
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 6,
                          background: sec.accent + "1a",
                          color: sec.accent,
                          border: `1px solid ${sec.accent}28`,
                        }}
                      >
                        {sec.code}
                      </span>
                      {completed.includes(step) && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: "#ecfdf5",
                            color: "#059669",
                            border: "1px solid #a7f3d0",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Check size={9} /> Complete
                        </span>
                      )}
                      {isLocked && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: "#f1f5f9",
                            color: "#64748b",
                            border: "1px solid #cbd5e1",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Lock size={9} /> View Only
                        </span>
                      )}
                    </div>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#334155",
                        marginTop: 3,
                        lineHeight: 1.55,
                      }}
                    >
                      {sec.desc}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                        marginTop: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          color: "#334155",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <FileText size={11} /> Step {step + 1} of {STEPS.length}
                      </span>
                      <div
                        style={{
                          marginLeft: "auto",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: sec.accent,
                          }}
                        >
                          {completed.includes(step) ? "Done" : "In progress"}
                        </span>
                        <div
                          style={{
                            width: 80,
                            height: 5,
                            borderRadius: 9,
                            background: "#f1f5f9",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: completed.includes(step) ? "100%" : "0%",
                              height: "100%",
                              background: sec.accent,
                              borderRadius: 9,
                              transition: "width 0.7s ease",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Panel content */}
              <div
                data-tour-id="tour-pia-form"
                style={{
                  position: "relative",
                  opacity: transitioning ? 0 : 1,
                  transform: transitioning ? "translateY(8px)" : "none",
                  transition: "opacity 0.22s ease, transform 0.22s ease",
                }}
              >
                {isLocked && (
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 5,
                      cursor: "not-allowed",
                      background:
                        "repeating-linear-gradient(135deg, rgba(148,163,184,0.05) 0px, rgba(148,163,184,0.05) 14px, rgba(148,163,184,0.09) 14px, rgba(148,163,184,0.09) 28px)",
                      borderRadius: 16,
                    }}
                  />
                )}
                <div
                  style={{
                    pointerEvents: formReadOnly ? "none" : "auto",
                    opacity: formReadOnly ? 0.72 : 1,
                    filter: formReadOnly
                      ? "grayscale(0.25) saturate(0.85)"
                      : "none",
                    userSelect: formReadOnly ? "text" : "auto",
                    transition: "opacity 0.2s ease, filter 0.2s ease",
                  }}
                >
                  {panels[sec.id]}
                </div>
                {isLocked && (
                  <div
                    style={{
                      position: "sticky",
                      bottom: 12,
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 14,
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "7px 16px",
                        borderRadius: 999,
                        background: "rgba(15,23,42,0.85)",
                        color: "#f8fafc",
                        fontSize: 11,
                        fontWeight: 600,
                        boxShadow: "0 6px 20px rgba(15,23,42,0.25)",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <Lock size={12} /> Submitted — read only
                    </div>
                  </div>
                )}
              </div>

              {/* Footer nav */}
              <div
                data-tour-id="tour-pia-nav-footer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: 24,
                  gap: 12,
                }}
              >
                <button
                  onClick={() => goTo(Math.max(0, step - 1))}
                  disabled={step === 0}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "9px 16px",
                    borderRadius: 12,
                    background: "#ffffff",
                    border: "1px solid rgba(99,102,241,0.18)",
                    color: step === 0 ? "#475569" : "#334155",
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: step === 0 ? "not-allowed" : "pointer",
                    opacity: step === 0 ? 0.4 : 1,
                    transition: "all 0.15s",
                    fontFamily: "inherit",
                  }}
                >
                  <ChevronLeft size={15} /> Previous
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  {STEPS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => goTo(i)}
                      style={{
                        height: 6,
                        borderRadius: 9,
                        border: "none",
                        cursor: "pointer",
                        width: i === step ? 22 : 6,
                        background:
                          i === step
                            ? sec.accent
                            : i < step
                              ? sec.accent + "55"
                              : "rgba(99,102,241,0.2)",
                        transition: "all 0.25s ease",
                        fontFamily: "inherit",
                      }}
                    />
                  ))}
                </div>

                {formReadOnly ? (
                  step < STEPS.length - 1 ? (
                    <button
                      onClick={() => goTo(step + 1)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "9px 18px",
                        borderRadius: 12,
                        background: "#ffffff",
                        color: "#334155",
                        fontSize: 12,
                        fontWeight: 600,
                        border: "1px solid rgba(99,102,241,0.18)",
                        cursor: "pointer",
                        transition: "all 0.15s",
                        fontFamily: "inherit",
                      }}
                    >
                      Next <ChevronRight size={15} />
                    </button>
                  ) : (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "9px 20px",
                        borderRadius: 12,
                        background: "#f1f5f9",
                        color: "#64748b",
                        fontSize: 12,
                        fontWeight: 700,
                        border: "1px solid #cbd5e1",
                      }}
                    >
                      <Lock size={14} />{" "}
                      {isLocked ? "Locked — Already Submitted" : "Cycle closed"}
                    </span>
                  )
                ) : step < STEPS.length - 1 ? (
                  <button
                    onClick={handleNext}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "9px 18px",
                      borderRadius: 12,
                      background: `linear-gradient(135deg,${sec.accent},${sec.accent}bb)`,
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      border: "none",
                      cursor: "pointer",
                      boxShadow: `0 4px 16px ${sec.accent}40`,
                      transition: "all 0.15s",
                      fontFamily: "inherit",
                    }}
                  >
                    Save & Next <ChevronRight size={15} />
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "9px 20px",
                      borderRadius: 12,
                      background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      border: "none",
                      cursor: "pointer",
                      boxShadow: "0 4px 20px rgba(99,102,241,0.45)",
                      transition: "all 0.15s",
                      fontFamily: "inherit",
                    }}
                  >
                    <ShieldCheck size={14} /> Submit PIA
                  </button>
                )}
              </div>
            </div>
        </main>
          </>
        )}
      </div>
    </div>
  );
}

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300..700;1,9..40,300..700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f8fafc; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: #e2e8f0; border-radius: 9px; }
  ::-webkit-scrollbar-thumb { background: linear-gradient(180deg, rgba(99,102,241,0.55), rgba(139,92,246,0.45)); border-radius: 9px; }
  ::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, rgba(99,102,241,0.75), rgba(139,92,246,0.65)); }
  input, textarea, select, button { font-family: inherit; }
  textarea { resize: vertical; }
  select option { background: #ffffff; color: #0f172a; }
  input[type=date]::-webkit-calendar-picker-indicator { opacity: 0.45; cursor: pointer; filter: saturate(0); }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
  @media (max-width: 900px) {
    .pia-mob-btn { display: flex !important; }
    .pia-desktop-sidebar { display: none !important; }
  }
`;
